import { useEffect, useMemo, useState } from 'react';
import {
  getFilingReadiness,
  type FilingProvider,
  type TaxFilingReadinessPayload,
} from '../api/tax-filing';

type TaxFilingReadinessRouteKey = 'tax-optimisation' | 'transactions';

function readSessionValue<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSessionValue(key: string, value: unknown | null) {
  if (typeof window === 'undefined') return;

  try {
    if (value == null) {
      window.sessionStorage.removeItem(key);
      return;
    }

    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // noop
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const responseError = (error as { response?: { data?: { error?: unknown } } }).response?.data?.error;
    if (typeof responseError === 'string' && responseError.trim()) return responseError;
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }

  return fallback;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isValidTaxFilingReadinessPayload(payload: unknown): payload is TaxFilingReadinessPayload {
  if (!isObject(payload)) return false;

  const proof = payload.proof;
  const dominantAction = payload.dominantAction;

  return (
    typeof payload.year === 'number'
    && typeof payload.provider === 'string'
    && typeof payload.verdict === 'string'
    && typeof payload.proofSummary === 'string'
    && isObject(dominantAction)
    && typeof dominantAction.label === 'string'
    && isObject(proof)
    && isObject(proof.lots)
    && isObject(proof.report)
    && isObject(proof.export)
    && isObject(proof.filing)
  );
}

function getTaxFilingReadinessStorageKey(
  routeKey: TaxFilingReadinessRouteKey,
  userId: string,
  year: number,
  provider: FilingProvider,
): string {
  return `tax-filing:readiness:${routeKey}:${userId}:${year}:${provider}`;
}

interface UseTaxFilingReadinessOptions {
  userId?: string;
  year: number;
  provider: FilingProvider;
  routeKey: TaxFilingReadinessRouteKey;
}

export interface UseTaxFilingReadinessResult {
  readiness: TaxFilingReadinessPayload | null;
  cachedReadiness: TaxFilingReadinessPayload | null;
  displayedReadiness: TaxFilingReadinessPayload | null;
  loading: boolean;
  error: string | null;
  recovered: boolean;
  rechecking: boolean;
  empty: boolean;
}

export function useTaxFilingReadiness({
  userId,
  year,
  provider,
  routeKey,
}: UseTaxFilingReadinessOptions): UseTaxFilingReadinessResult {
  const storageKey = useMemo(
    () => (userId ? getTaxFilingReadinessStorageKey(routeKey, userId, year, provider) : null),
    [provider, routeKey, userId, year],
  );
  const [cachedReadiness, setCachedReadiness] = useState<TaxFilingReadinessPayload | null>(null);
  const [restoredFromStorage, setRestoredFromStorage] = useState(false);
  const [readiness, setReadiness] = useState<TaxFilingReadinessPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!storageKey) {
      setCachedReadiness(null);
      setRestoredFromStorage(false);
      return;
    }

    const storedReadiness = readSessionValue<TaxFilingReadinessPayload>(storageKey);
    setCachedReadiness(storedReadiness);
    setRestoredFromStorage(Boolean(storedReadiness));
  }, [storageKey]);

  useEffect(() => {
    if (!userId) {
      setReadiness(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    getFilingReadiness(userId, { year, provider })
      .then((payload) => {
        if (cancelled) return;

        if (!isValidTaxFilingReadinessPayload(payload)) {
          setReadiness(null);
          setError('Unable to load filing readiness right now.');
          return;
        }

        setReadiness(payload);
        if (storageKey) {
          writeSessionValue(storageKey, payload);
        }
      })
      .catch((fetchError: unknown) => {
        if (cancelled) return;

        setReadiness(null);
        setError(getErrorMessage(fetchError, 'Unable to load filing readiness right now.'));
      })
      .finally(() => {
        if (cancelled) return;

        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [provider, storageKey, userId, year]);

  const displayedReadiness = readiness ?? cachedReadiness;
  const rechecking = loading && Boolean(cachedReadiness);
  const recovered = Boolean(readiness && displayedReadiness && restoredFromStorage && !loading && !error);
  const empty = !loading && !displayedReadiness && !error;

  return {
    readiness,
    cachedReadiness,
    displayedReadiness,
    loading,
    error,
    recovered,
    rechecking,
    empty,
  };
}
